import React from "react";
import { EvaluationResult } from "../../../data-model/nccnGuidelines";
import { isExceptionFlowForRadiologyOrg } from "../../../enums/organizationTypes";
import { useFacilityCategory } from "../../../hooks/useOncoemrTasksEnabled";
import { ReviewProgress } from "../../medicalNecessityAnalysis/utils/nccnReviewFeedback";
import {
  VerdictTone,
  VERDICT_COLORS,
} from "../../medicalNecessityAnalysis/utils/verdictColors";
import { CircularProgressRing } from "./CircularProgressRing";
import IfFutureVisitWillExhaustBadge from "./ifFutureVisitWillExhaustBadge";
import MultiplePastRegimenBadge from "./multiplePastRegimenBadge";

/** Human-readable label for each order-level verdict shown on the action. */
const VERDICT_LABEL: Record<VerdictTone, string> = {
  met: "Met",
  notMet: "Not Met",
  undetermined: "Undetermined",
};

/**
 * Small pill showing the order-level medical-necessity verdict, shown on the
 * action once every guideline has been reviewed.
 *
 * @param props.result - Order-level evaluation result (`True`/`False`/else)
 * @returns The rendered verdict pill
 */
const MedicalNecessityVerdictTag: React.FC<{
  result: EvaluationResult | null;
}> = ({ result }) => {
  const tone: VerdictTone =
    result === "True" ? "met" : result === "False" ? "notMet" : "undetermined";
  const { background, text } = VERDICT_COLORS[tone];
  const label = VERDICT_LABEL[tone];
  return (
    <span
      className="rounded px-2 py-0.5 text-xs font-semibold"
      style={{ backgroundColor: background, color: text }}
    >
      {label}
    </span>
  );
};

interface RegimenInfoBarProps {
  regimenData: string[];
  ifMultiplePastRegimenExists: boolean;
  potentialExhaustVisits: boolean;
  onViewTxPlanClick: () => void;
  /** Handler for the "Medical Necessity" action; navigates to the analysis screen. */
  onMedicalNecessityClick: () => void;
  /**
   * Guideline review progress for this order, or null for a fresh order that
   * has no stored evaluation yet. The action shows the forward arrow until
   * review actually starts (`hasStarted`); once some review is submitted it
   * shows the progress ring, and the verdict tag once review is complete.
   */
  reviewProgress?: ReviewProgress | null;
  /**
   * True while {@link reviewProgress} is still being fetched. The action shows a
   * single spinner in its trailing slot until this resolves and then swaps
   * straight to the final indicator (arrow / ring / verdict), so the user never
   * sees a misleading intermediate arrow or an extra layout shift.
   */
  isReviewProgressLoading?: boolean;
  /**
   * Handler for the "View Order Document" action. Only rendered for
   * radiology exception-flow facilities (`category === "radiology"`), which
   * store a radiology order PDF; ignored otherwise.
   */
  onClickViewOncoDocument?: () => void;
  /**
   * Whether the "Medical Necessity" button is shown. Gated by the live NCCN
   * case-gate config (auth status / regimen / ICD); defaults to hidden.
   */
  showMedicalNecessity?: boolean;
  /**
   * Handler for the "View Clinical Document" action. Only rendered when a
   * clinical document is available on the order; opens it in the doc viewer.
   */
  onClickViewClinicalDocument?: () => void;
}

/**
 * Top info bar shown above an order's content. Displays the regimen
 * summary alongside contextual badges and quick actions ("View Tx Plan",
 * and for radiology orgs, "View Order Document").
 */
const RegimenInfoBar: React.FC<RegimenInfoBarProps> = ({
  regimenData,
  ifMultiplePastRegimenExists,
  potentialExhaustVisits,
  onViewTxPlanClick,
  onMedicalNecessityClick,
  onClickViewOncoDocument,
  reviewProgress,
  isReviewProgressLoading = false,
  showMedicalNecessity = false,
  onClickViewClinicalDocument,
}) => {
  const facilityCategory = useFacilityCategory();
  return (
    <div className="mt-1 flex items-center gap-3">
      <div className="flex flex-1 items-center justify-between bg-yellow-50 p-3 text-gray-600">
        <div className="text-xs">{regimenData}</div>
        <div className="action-container-with-multiple-past-regimen-flag flex items-center gap-3">
          {ifMultiplePastRegimenExists && <MultiplePastRegimenBadge />}
          {potentialExhaustVisits && <IfFutureVisitWillExhaustBadge />}
          <div
            className="cursor-pointer text-sm font-bold text-tertiaryBlue-4 hover:cursor-pointer hover:text-tertiaryBlue-5"
            onClick={onViewTxPlanClick}
          >
            View Tx Plan
          </div>
          {isExceptionFlowForRadiologyOrg(facilityCategory) &&
            onClickViewOncoDocument && (
              <div
                className="view-order-document--btn cursor-pointer text-sm font-bold text-tertiaryBlue-4 hover:cursor-pointer hover:text-tertiaryBlue-5"
                onClick={onClickViewOncoDocument}
              >
                View Order Document
              </div>
            )}
          {isExceptionFlowForRadiologyOrg(facilityCategory) &&
            onClickViewClinicalDocument && (
              <div
                className="view-clinical-document--btn cursor-pointer text-sm font-bold text-tertiaryBlue-4 hover:cursor-pointer hover:text-tertiaryBlue-5"
                onClick={onClickViewClinicalDocument}
              >
                View Clinical Document
              </div>
            )}
        </div>
      </div>
      {showMedicalNecessity && (
        <button
          type="button"
          className="flex shrink-0 items-center gap-2 rounded-[4px] border border-primaryGray-2 bg-white px-4 py-2 text-sm font-bold text-primaryGray-2"
          onClick={onMedicalNecessityClick}
        >
          Medical Necessity
          {isReviewProgressLoading ? (
            <span
              className="h-[18px] w-[18px] shrink-0 animate-spin rounded-full border-2 border-primaryGray-15 border-t-tertiaryBlue-4"
              role="status"
              aria-label="Loading review progress"
            />
          ) : !reviewProgress || !reviewProgress.hasStarted ? (
            <span aria-hidden="true">&rarr;</span>
          ) : reviewProgress.isComplete ? (
            <MedicalNecessityVerdictTag result={reviewProgress.orderResult} />
          ) : reviewProgress.anyHumanMet ? (
            // One Human-Met guideline already settles the order as Met, so
            // surface the Met tag instead of the remaining progress.
            <MedicalNecessityVerdictTag result="True" />
          ) : (
            <CircularProgressRing fraction={reviewProgress.fraction} />
          )}
        </button>
      )}
    </div>
  );
};

export default RegimenInfoBar;
